import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Send, CheckCircle2, Copy, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { api } from "../lib/site";
import { fileToCompressedDataUrl } from "../lib/image";

const PC_DEVICES = ["Desktop PC", "Gaming PC"];

export default function BookingForm() {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    device_type: "",
    service_type: "",
    delivery_method: "",
    issue_description: "",
    preferred_date: null,
    photos: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/services").then((res) => {
      setMeta(res.data);
      // Pre-fill from URL query params (e.g., /?device=Controller&service=Controller+Repair#booking)
      try {
        const params = new URLSearchParams(window.location.search);
        const device = params.get("device");
        const service = params.get("service");
        const delivery = params.get("delivery");
        const updates = {};
        if (device && res.data.device_types.includes(device)) updates.device_type = device;
        if (service && res.data.service_types.includes(service)) updates.service_type = service;
        if (delivery && res.data.delivery_methods.includes(delivery)) updates.delivery_method = delivery;
        if (Object.keys(updates).length > 0) {
          setForm((f) => ({ ...f, ...updates }));
        }
      } catch (err) {
        console.error("Failed to load booking meta:", err);
      }
    }).catch((err) => console.error("Booking meta fetch failed:", err));
  }, []);

  const isPc = PC_DEVICES.includes(form.device_type);
  const allowedDelivery = (meta?.delivery_methods || []).filter(
    (m) => !(isPc && m === "ship-in")
  );

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = 5 - form.photos.length;
    if (remaining <= 0) {
      toast.error("Max 5 photos");
      return;
    }
    setUploadingPhoto(true);
    try {
      const incoming = Array.from(files).slice(0, remaining);
      const compressed = await Promise.all(
        incoming.map((f) => fileToCompressedDataUrl(f, { maxWidth: 1280, quality: 0.7 }))
      );
      setForm((f) => ({ ...f, photos: [...f.photos, ...compressed] }));
    } catch (e) {
      toast.error("Could not process image");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.phone || !form.device_type ||
        !form.service_type || !form.delivery_method || !form.issue_description) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: form.preferred_date ? format(form.preferred_date, "yyyy-MM-dd") : null,
      };
      const res = await api.post("/bookings", payload);
      setSuccess(res.data);
      toast.success(`Booking received — Ticket ${res.data.ticket_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyTicket = () => {
    if (success?.ticket_id) {
      navigator.clipboard.writeText(success.ticket_id);
      toast.success("Ticket number copied");
    }
  };

  const [payingDeposit, setPayingDeposit] = useState(false);
  const startDeposit = async () => {
    if (!success?.ticket_id) return;
    setPayingDeposit(true);
    // Prefer the URL we got from the booking response — otherwise fall back to the API.
    if (success.deposit_checkout_url) {
      window.location.href = success.deposit_checkout_url;
      return;
    }
    try {
      const res = await api.post("/payments/checkout/deposit", {
        ticket_id: success.ticket_id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setPayingDeposit(false);
    }
  };

  if (success) {
    const isShipIn = success.delivery_method === "ship-in";
    const depositRequired = isShipIn && !success.deposit_paid;
    return (
      <section id="booking" className="py-24 sm:py-32 border-t border-white/5" data-testid="booking-success-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          {depositRequired ? (
            <>
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#FFCC00] mb-4" data-testid="deposit-required-banner">
                ⚠ ACTION REQUIRED · $30 DEPOSIT TO CONFIRM
              </div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-4">
                One last step.
              </h2>
              <p className="text-zinc-400 mb-10 leading-relaxed max-w-xl mx-auto">
                Mail-in repairs require a <strong className="text-white">$30 deposit</strong> to reserve your workbench slot.
                It's applied toward your final repair total. Your booking isn't confirmed until the deposit is paid.
              </p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-12 h-12 text-[#34C759] mx-auto mb-6" />
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
                REQUEST RECEIVED
              </div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-6">
                You're on the list.
              </h2>
              <p className="text-zinc-400 mb-10 leading-relaxed">
                I'll review your request and reach out shortly with next steps. Save this ticket
                number — you can track repair status anytime in the tracker section.
              </p>
            </>
          )}

          <div className="inline-flex items-center gap-3 bg-[#121212] border border-zinc-800 rounded-md px-6 py-4 font-mono">
            <span className="text-zinc-500 text-xs tracking-[0.2em] uppercase">TICKET</span>
            <span className="text-[#FFCC00] text-2xl font-bold" data-testid="success-ticket-id">
              {success.ticket_id}
            </span>
            <Button onClick={copyTicket} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
              <Copy className="w-4 h-4" />
            </Button>
          </div>

          {depositRequired && (
            <div className="mt-8 bg-[#121212] border-2 border-[#0055FF] rounded-md p-6 sm:p-8 max-w-xl mx-auto" data-testid="deposit-block">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#0055FF] mb-3">
                MAIL-IN RESERVATION DEPOSIT · $30
              </div>
              <Button
                onClick={startDeposit}
                disabled={payingDeposit}
                size="lg"
                className="bg-[#0055FF] hover:bg-blue-600 text-white font-semibold w-full text-base py-6"
                data-testid="deposit-pay-btn"
              >
                {payingDeposit ? "Redirecting to Stripe…" : "Pay $30 deposit · Stripe checkout"}
              </Button>
              <p className="font-mono text-[10px] text-zinc-500 mt-3 text-center">
                Secure checkout · Stripe handles your card details, never our server.
              </p>
              <p className="text-zinc-400 text-xs mt-4 leading-relaxed text-center">
                We also emailed you the same payment link — you can complete this from your inbox if you prefer.
              </p>
              <div className="mt-4 pt-4 border-t border-zinc-800 text-[11px] text-zinc-400 leading-relaxed" data-testid="deposit-refund-policy">
                <span className="text-[#FFCC00] font-semibold">Refund policy:</span> the $30 deposit is applied toward your final repair total.
                If you decline the repair after diagnostic, <strong>$15 is retained as a non-refundable diagnostic fee</strong> and the remaining $15 is refunded to your original payment method.
              </div>
            </div>
          )}

          {!depositRequired && (
            <div className="mt-10">
              <Button
                onClick={() => setSuccess(null)}
                variant="outline"
                className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white"
                data-testid="booking-new-btn"
              >
                Submit another request
              </Button>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-24 sm:py-32 border-t border-white/5" data-testid="booking-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="mb-16">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
            [ 07 ] · BOOK A REPAIR
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
            Tell me what broke.
          </h2>
          <p className="text-zinc-400 max-w-xl">
            One form. I'll respond personally to confirm and walk you through next steps.
          </p>
          <div
            className="mt-6 inline-flex items-start gap-3 bg-[#0055FF]/10 border border-[#0055FF]/30 rounded-md px-4 py-3 max-w-xl"
            data-testid="booking-hours-notice"
          >
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#0055FF] pt-0.5">
              FYI
            </span>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Closed Mon–Fri for in-shop work — message or call me anytime and I'll get back to you. Drop-offs and pickups happen <span className="text-white font-medium">Saturday nights and all day Sunday</span>.
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-[#121212] border border-zinc-800 rounded-md p-6 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="booking-form"
        >
          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Your name *</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => update("customer_name", e.target.value)}
              placeholder="Jane Doe"
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="booking-name"
            />
          </div>

          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com"
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="booking-email"
            />
          </div>

          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Phone *</Label>
            <Input
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="(607) 555-0123"
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF]"
              data-testid="booking-phone"
            />
          </div>

          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Preferred date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start bg-[#0A0A0A] border-zinc-800 text-white hover:bg-[#0A0A0A] hover:text-white font-normal"
                  data-testid="booking-date-trigger"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-[#0055FF]" />
                  {form.preferred_date ? format(form.preferred_date, "PPP") : "Pick a date (optional)"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#121212] border-zinc-800" align="start">
                <Calendar
                  mode="single"
                  selected={form.preferred_date}
                  onSelect={(d) => update("preferred_date", d)}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Device type *</Label>
            <Select value={form.device_type} onValueChange={(v) => {
              update("device_type", v);
              if (PC_DEVICES.includes(v) && form.delivery_method === "ship-in") {
                update("delivery_method", "");
              }
            }}>
              <SelectTrigger
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:ring-[#0055FF]"
                data-testid="booking-device"
              >
                <SelectValue placeholder="Select device" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                {meta?.device_types?.map((d) => (
                  <SelectItem key={d} value={d} className="focus:bg-white/5 focus:text-white">{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Service *</Label>
            <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
              <SelectTrigger
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:ring-[#0055FF]"
                data-testid="booking-service"
              >
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                {meta?.service_types?.map((d) => (
                  <SelectItem key={d} value={d} className="focus:bg-white/5 focus:text-white">{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Delivery method *</Label>
            <Select value={form.delivery_method} onValueChange={(v) => update("delivery_method", v)}>
              <SelectTrigger
                className="bg-[#0A0A0A] border-zinc-800 text-white focus:ring-[#0055FF]"
                data-testid="booking-delivery"
              >
                <SelectValue placeholder="How will the device get to me?" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-zinc-800 text-white">
                {allowedDelivery.map((d) => (
                  <SelectItem key={d} value={d} className="focus:bg-white/5 focus:text-white capitalize">
                    {d.replace("-", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isPc && (
              <p className="text-[#FFCC00] text-xs mt-2 font-mono">
                ! PC repairs are in-person only. Drop off at 67 Grand St, Susquehanna PA.
              </p>
            )}
            {form.delivery_method === "ship-in" && !isPc && (
              <div className="mt-3 bg-[#FFCC00]/5 border border-[#FFCC00]/30 rounded-md p-3 text-[11px] text-zinc-300 leading-relaxed" data-testid="ship-in-deposit-disclaimer">
                <strong className="text-[#FFCC00]">Mail-in policy:</strong> a <strong>$30 deposit is required</strong> to confirm your booking and reserve your slot.
                It's applied toward your final repair total. If you decline the repair after diagnostic,
                <strong> $15 is retained as a non-refundable diagnostic fee</strong> and the remaining $15 is refunded to your original payment method.
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">Describe the issue *</Label>
            <Textarea
              value={form.issue_description}
              onChange={(e) => update("issue_description", e.target.value)}
              placeholder="My PS5 makes a loud grinding noise on startup and won't read discs..."
              rows={5}
              className="bg-[#0A0A0A] border-zinc-800 text-white focus:border-[#0055FF] focus:ring-[#0055FF] resize-none"
              data-testid="booking-issue"
            />
          </div>

          <div className="md:col-span-2">
            <Label className="text-zinc-400 font-mono text-xs tracking-[0.15em] uppercase mb-2 block">
              Photos (optional — max 5)
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {form.photos.map((src, idx) => (
                <div
                  key={idx}
                  className="relative aspect-square rounded-md overflow-hidden border border-zinc-800 group"
                  data-testid={`booking-photo-${idx}`}
                >
                  <img src={src} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 bg-black/80 hover:bg-[#FF3B30] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove photo"
                    data-testid={`booking-photo-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {form.photos.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="aspect-square rounded-md border-2 border-dashed border-zinc-800 hover:border-[#0055FF] hover:bg-white/5 transition-colors flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-white"
                  data-testid="booking-photo-add"
                >
                  <ImagePlus className="w-6 h-6" />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase">
                    {uploadingPhoto ? "Processing..." : "Add photo"}
                  </span>
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => addPhotos(e.target.files)}
              className="hidden"
              data-testid="booking-photo-input"
            />
            <p className="font-mono text-[10px] text-zinc-600 mt-2">
              Photos are compressed in your browser before upload. Helps me pre-diagnose.
            </p>
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-zinc-800">
            <p className="font-mono text-xs text-zinc-500 max-w-md">
              Submitting sends your request to me directly. I'll reply with a quote and timeline within 24h.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0055FF] hover:bg-blue-600 text-white rounded-md font-semibold px-8 py-6 text-base"
              data-testid="booking-submit"
            >
              {submitting ? "Sending..." : <>Submit request <Send className="ml-2 w-4 h-4" /></>}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
